// Runtime for the logic graphs authored in the admin "Logic" editor. Subscribes to
// the whole `logic` collection (one graph per app page) and executes EVERY attached
// graph deterministically when a trigger fires. The wire contract (node `type`
// strings, `config` keys, port names) MUST match admin/src/lib/logic.ts. Every
// failure is contained — a broken/absent graph or a failing action NEVER breaks the
// app flow that fired the trigger.
import { get } from 'svelte/store';
import { collection, onSnapshot } from 'firebase/firestore';
import { fbDb, fbAuth } from './firebase';
import { phr, imports as importsStore, studyMovies, addPhrRecord, addOrMergeCondition } from './stores';
import { parseDate } from './labTable';
import { pushToast } from './toast';
import { plotRequest } from './plotRequest';
import { reportRequest } from './reportRequest';

interface GNode { id: string; type: string; config: Record<string, string>; }
interface GEdge { from: string; fromPort: string; to: string; }
interface Graph { nodes: GNode[]; edges: GEdge[]; }

let graphs: Graph[] = [];
let subscribed = false;
let running = false;

/** Subscribe once to the `logic` collection; keeps `graphs` current. */
export function initLogic(): void {
  if (subscribed) return;
  subscribed = true;
  try {
    onSnapshot(
      collection(fbDb, 'logic'),
      (snap) => {
        graphs = snap.docs.map((doc) => {
          const d = doc.data() as Partial<Graph>;
          return { nodes: Array.isArray(d.nodes) ? (d.nodes as GNode[]) : [], edges: Array.isArray(d.edges) ? (d.edges as GEdge[]) : [] };
        });
      },
      () => { /* denied / offline → no automations, app unaffected */ },
    );
  } catch { /* ignore */ }
}

const today = () => { const d = new Date(); return `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`; };
function tmpl(s: string | undefined, event: Record<string, unknown>): string {
  return String(s ?? '').replace(/\{\{(\w+)\}\}/g, (_, k) => String(event[k] ?? ''));
}

function srcRows(src: string): unknown[] {
  const p = get(phr);
  switch (src) {
    case 'profile': return [p.owner];
    case 'visits': return p.wrappers;
    case 'imports': return get(importsStore);
    case 'movies': return get(studyMovies);
    default: { const r = (p as unknown as Record<string, unknown>)[src]; return Array.isArray(r) ? r : []; }
  }
}
function runData(n: GNode, event: Record<string, unknown>): void {
  if (n.type === 'data.source') {
    const src = n.config.source || 'conditions';
    event.rows = srcRows(src);
    event.count = (event.rows as unknown[]).length;
    event.source = src;
  } else if (n.type === 'data.select') {
    const field = n.config.field || 'description';
    const text = String(n.config.text || '').toLowerCase();
    const rows = Array.isArray(event.rows) ? (event.rows as Record<string, unknown>[]) : [];
    event.rows = text ? rows.filter((r) => String(r?.[field] ?? '').toLowerCase().includes(text)) : rows;
    event.count = (event.rows as unknown[]).length;
  }
}

function evalCondition(n: GNode, event: Record<string, unknown>): boolean {
  if (n.type === 'cond.textContains') {
    const field = n.config.field || 'summary';
    const text = String(n.config.text || '').toLowerCase();
    return text ? String(event[field] ?? '').toLowerCase().includes(text) : false;
  }
  if (n.type === 'cond.hasConditions') return Number(event.conditionCount ?? 0) > 0;
  return false;
}

async function runAction(n: GNode, event: Record<string, unknown>): Promise<void> {
  if (n.type === 'action.addNote') {
    const title = (tmpl(n.config.title, event) || 'Automation').slice(0, 80);
    await addPhrRecord('notes', { date: today(), title, body: tmpl(n.config.body, event), author: 'logic' });
  } else if (n.type === 'action.notify') {
    pushToast(tmpl(n.config.message, event) || 'Automation ran');
  } else if (n.type === 'action.trackCondition') {
    const description = tmpl(n.config.description, event).trim();
    if (description) {
      const code = 'LOGIC-' + description.toLowerCase().replace(/[^a-z0-9]+/g, '-').slice(0, 24);
      await addOrMergeCondition({ code, description, status: n.config.status || 'Active', since: '', provider: 'logic' });
    }
  } else if (n.type === 'action.anatomy') {
    const region = String(n.config.region || 'abdomen');
    const label = tmpl(n.config.label, event).trim() || 'Finding';
    await addOrMergeCondition({ code: `LOGIC-ANAT-${region}`, description: label, status: n.config.status || 'Active', since: '', region, provider: 'logic' });
  } else if (n.type === 'action.plot') {
    const metric = String(n.config.metric || '').toLowerCase();
    const vs = get(phr).vitals.find((v) => v.key.toLowerCase() === metric || (v.label || '').toLowerCase() === metric);
    if (vs) {
      const points = vs.samples
        .map((s) => ({ t: parseDate(s.date) ?? NaN, y: s.value }))
        .filter((p) => Number.isFinite(p.t) && Number.isFinite(p.y));
      if (points.length) plotRequest.set({ title: vs.label || vs.key, unit: vs.unit || '', series: [{ color: '#C9A86A', points }] });
    }
  } else if (n.type === 'action.report') {
    const path = String(event.reportPath || '');
    if (path) reportRequest.set({ name: String(event.reportName || event.name || 'Report'), path });
  } else if (n.type === 'ai.infer') {
    // Claude inference via the app's chat endpoint; result → {{ai}} for downstream nodes.
    const prompt = tmpl(n.config.prompt, event).trim();
    if (!prompt) return;
    try {
      const token = await fbAuth.currentUser?.getIdToken();
      const res = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'content-type': 'application/json', ...(token ? { authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ messages: [{ role: 'me', text: prompt }], context: '' }),
      });
      const data = await res.json().catch(() => ({} as Record<string, unknown>));
      event.ai = String((data as Record<string, unknown>).text || (data as Record<string, unknown>).reply || '');
    } catch (e) { console.error('[logic] ai.infer failed', e); event.ai = ''; }
  }
}

async function walk(g: Graph, node: GNode, port: string, event: Record<string, unknown>, visited: Set<string>, depth: number): Promise<void> {
  if (depth > 200) return;
  for (const e of g.edges.filter((x) => x.from === node.id && x.fromPort === port)) {
    const next = g.nodes.find((n) => n.id === e.to);
    if (!next || visited.has(next.id)) continue;
    visited.add(next.id);
    await execNode(g, next, event, visited, depth + 1);
  }
}
async function execNode(g: Graph, node: GNode, event: Record<string, unknown>, visited: Set<string>, depth: number): Promise<void> {
  if (node.type.startsWith('cond.')) {
    await walk(g, node, evalCondition(node, event) ? 'true' : 'false', event, visited, depth);
  } else if (node.type.startsWith('data.')) {
    try { runData(node, event); } catch (e) { console.error('[logic] data failed', node.type, e); }
    await walk(g, node, 'out', event, visited, depth);
  } else if (node.type.startsWith('action.') || node.type.startsWith('ai.')) {
    try { await runAction(node, event); } catch (e) { console.error('[logic] action failed', node.type, e); }
    await walk(g, node, 'out', event, visited, depth);
  }
}

/** Fire a trigger through EVERY attached graph. Re-entrancy-guarded, contained. */
export async function runTrigger(triggerKey: string, event: Record<string, unknown>): Promise<void> {
  initLogic();
  if (running) return;
  running = true;
  try {
    for (const g of graphs) {
      for (const t of g.nodes.filter((n) => n.type === triggerKey)) {
        await walk(g, t, 'out', { ...event }, new Set([t.id]), 0);
      }
    }
  } catch (e) {
    console.error('[logic] run failed', e);
  } finally {
    running = false;
  }
}
